import React from 'react';
import { useSelector } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import { ListItem } from 'src/components/list-item';
import { PanelHeader, PanelTitle } from '@prisjakt/ui/panel';

import FilterPanel from './components/panel';
import FilterToggle from '../filter-toggle';

import type { AppState } from 'src/reducers';
import type { Category } from '../sub-category-listing/types';
import type { Property } from './types';

import * as S from './styles';
import { TextButton } from '@prisjakt/ui/button/variants';

type HorizontalFilterProps = {
  brand?: unknown;
  productCount?: number;
  className?: string;
  quickFilters: Category[];
  canCompare?: boolean;
  expanded?: boolean;
  toggleFilter: () => void;
};

type SearchState = {
  inputValue: string;
  filteredCore: Property[];
  filteredSections: Property[];
  filteredValues: Record<string, any>;
  disabledExpansions: string[];
};

const HorizontalFilter: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<HorizontalFilterProps>>
> = props => {
  const [showAll, setShowAll] = React.useState(false);

  const filter = useSelector((state: AppState) => state.areas.category.filter);
  const { expandedCategories } = filter;

  const { t } = useAppContext();

  const search = useSelector(
    (state: AppState) => state.areas.category.search as SearchState
  );
  const {
    inputValue,
    filteredCore,
    filteredSections,
    filteredValues,
    disabledExpansions,
  } = search;

  const isBrandedCategory = Boolean(props.brand);
  const isSearching = inputValue.length > 1;

  return (
    <>
      <S.Header>
        <PanelHeader>
          <PanelTitle>{t('filter', 'filter.title')}</PanelTitle>
          <FilterToggle
            toggleHorizontalFilter={props.toggleFilter}
            expanded={props.expanded}
          />
        </PanelHeader>
      </S.Header>

      {props.quickFilters.length > 0 && (
        <div>
          {props.quickFilters.map(c => (
            <ListItem key={c.name} name={c.name} link={c.pathName} showArrow />
          ))}
        </div>
      )}

      <S.Filter horizontal>
        <div>
          {filteredCore.map(coreFilter => (
            <FilterPanel
              key={coreFilter.id}
              layout="horizontal"
              canCompare={props.canCompare}
              // compareLimit={8}
              isBrandFilteredCategory={isBrandedCategory}
              displayFilter={coreFilter}
              filteredValues={filteredValues}
            />
          ))}
        </div>
        <S.MoreFilters>
          <TextButton onClick={() => setShowAll(prevState => !prevState)}>
            {t(
              'filter',
              !showAll
                ? 'properties.quantity-show-more'
                : 'properties.quantity-show-less'
            )}
          </TextButton>
        </S.MoreFilters>
        <div>
          {filteredSections.map(section => {
            const showSection =
              showAll || isSearching
                ? disabledExpansions.indexOf(section.name) < 0 // Defaults to expanded
                : expandedCategories.indexOf(section.name) >= 0; // Defaults to collapsed

            // Remove empty sections
            if (
              section.children?.filter(
                subFilter =>
                  (subFilter.item_type === 'category' && subFilter.children) ||
                  (filteredValues &&
                    filteredValues[subFilter.id] &&
                    filteredValues[subFilter.id].max_matching_products)
              ).length === 0
            ) {
              return null;
            }

            if (!showSection) {
              return null;
            }

            return (
              <FilterPanel
                key={section.name}
                layout="horizontal"
                // compareLimit={8}
                // name={section.name}
                isBrandFilteredCategory={isBrandedCategory}
                displayFilter={section}
                filteredValues={filteredValues}
                hideName
              />
            );
          })}
        </div>
      </S.Filter>
    </>
  );
};

export default HorizontalFilter;
